import type {
  RestProduct,
  RestUpdateProduct,
} from "@KassaroAB/shared/types/product";
import type { Money } from "@KassaroAB/shared/types/money";
import type { ProductVariant } from "@KassaroAB/shared/types/productVariant";
import type { ProductVariantId } from "@KassaroAB/shared/types/ids";
import {
  ProductOverlayFieldScheme,
  ProductVariantOverlayFieldScheme,
  RestUpsertProductOverlay,
} from "@KassaroAB/shared/types/productOverlay";

const normalizeOptionalString = (value: string | null | undefined): string =>
  value ?? "";

const moneyEqual = (
  original: Money | null | undefined,
  edited: Money | null | undefined,
): boolean => {
  if (original == null && edited == null) {
    return true;
  }
  if (original == null || edited == null) {
    return false;
  }
  return (
    original.amount === edited.amount && original.currency === edited.currency
  );
};

const barcodeEqual = (
  original: RestProduct["barcode"],
  edited: RestUpdateProduct["barcode"],
): boolean => {
  const normalize = (
    barcode: RestProduct["barcode"] | RestUpdateProduct["barcode"],
  ) => ({
    code: barcode?.code ?? "",
    type: barcode?.type ?? null,
    format: barcode?.format ?? null,
  });
  const left = normalize(original);
  const right = normalize(edited);
  return (
    left.code === right.code &&
    left.type === right.type &&
    left.format === right.format
  );
};

const extraFieldsEqual = (
  original: Record<string, string> | null | undefined,
  edited: Record<string, string> | null | undefined,
): boolean => {
  const normalize = (fields: Record<string, string> | null | undefined) => {
    const entries = Object.entries(fields ?? {}).filter(
      ([, value]) => value.trim() !== "",
    );
    entries.sort(([a], [b]) => a.localeCompare(b));
    return Object.fromEntries(entries);
  };
  return (
    JSON.stringify(normalize(original)) === JSON.stringify(normalize(edited))
  );
};

const nullableIdEqual = (
  original: string | null | undefined,
  edited: string | null | undefined,
): boolean => (original ?? null) === (edited ?? null);

export type EditableVariantField = Exclude<
  keyof ProductVariant,
  "id" | "shelfLocation" | "productSyncExternalId"
>;

const isVariantFieldValueChanged = (
  original: ProductVariant | undefined,
  edited: ProductVariant,
  field: EditableVariantField,
): boolean => {
  switch (field) {
    case "unitPrice":
    case "purchasePrice":
    case "takeawayPrice":
      return !moneyEqual(original?.[field], edited[field]);
    case "barcode":
      return !barcodeEqual(original?.barcode, edited.barcode);
    case "extraFields":
      return !extraFieldsEqual(original?.extraFields, edited.extraFields);
    case "stockCount":
      return (original?.stockCount ?? null) !== (edited.stockCount ?? null);
    case "serialTracking":
      return (
        (original?.serialTracking ?? false) !== (edited.serialTracking ?? false)
      );
    case "stockPolicy":
      return (
        JSON.stringify(original?.stockPolicy ?? null) !==
        JSON.stringify(edited.stockPolicy ?? null)
      );
    case "name":
    case "articleNumber":
    case "manufacturerBarcode":
      return (
        normalizeOptionalString(original?.[field]) !==
        normalizeOptionalString(edited[field])
      );
  }
};

const EDITABLE_VARIANT_FIELDS: EditableVariantField[] = [
  "name",
  "articleNumber",
  "manufacturerBarcode",
  "unitPrice",
  "purchasePrice",
  "takeawayPrice",
  "barcode",
  "extraFields",
  "stockCount",
  "stockPolicy",
];

// The backend keeps these when the key is missing, so unchanged ones are left
// out instead of overwriting concurrent changes.
const BACKEND_PRESERVED_VARIANT_FIELDS = ["stockCount", "stockPolicy"] as const;

const variantsEqual = (
  original: ProductVariant[] | undefined,
  edited: ProductVariant[] | undefined,
): boolean => {
  const left = original ?? [];
  const right = edited ?? [];
  return (
    left.length === right.length &&
    right.every(
      (variant, index) =>
        variant.id === left[index].id &&
        EDITABLE_VARIANT_FIELDS.every(
          (field) => !isVariantFieldValueChanged(left[index], variant, field),
        ),
    )
  );
};

export const omitUnchangedVariantStock = (
  original: ProductVariant[] | undefined,
  variants: ProductVariant[],
): ProductVariant[] => {
  const originalById = new Map(
    (original ?? []).map((variant) => [variant.id, variant]),
  );
  return variants.map((variant) => {
    const existing = originalById.get(variant.id);
    if (!existing) {
      return variant;
    }
    const withoutStock = { ...variant };
    BACKEND_PRESERVED_VARIANT_FIELDS.forEach((field) => {
      if (!isVariantFieldValueChanged(existing, variant, field)) {
        delete withoutStock[field];
      }
    });
    return withoutStock;
  });
};

export const isProductFieldValueChanged = (
  product: RestProduct,
  field: keyof RestUpdateProduct,
  value: unknown,
): boolean => {
  switch (field) {
    case "name":
      return value !== product.name;
    case "articleNumber":
      return (
        normalizeOptionalString(value as string) !==
        normalizeOptionalString(product.articleNumber)
      );
    case "unitPrice":
      return !moneyEqual(product.unitPrice, value as Money);
    case "VAT":
      return value !== product.VAT;
    case "type":
      return value !== product.type;
    case "groupId":
      return !nullableIdEqual(product.groupId, value as string | null);
    case "stockCount":
      return (value as number | null) !== (product.stockCount ?? null);
    case "unitId":
      return !nullableIdEqual(product.unitId, value as string | null);
    case "openPrice":
      return value !== product.openPrice;
    case "bookkeepingAccountId":
      return !nullableIdEqual(
        product.bookkeepingAccountId,
        value as string | null,
      );
    case "bookkeepingCostcenter":
      return (
        normalizeOptionalString(value as string) !==
        normalizeOptionalString(product.bookkeepingCostcenter)
      );
    case "information":
      return (
        normalizeOptionalString(value as string) !==
        normalizeOptionalString(product.information)
      );
    case "purchasePrice":
      return !moneyEqual(product.purchasePrice ?? null, value as Money | null);
    case "barcode":
      return !barcodeEqual(
        product.barcode,
        value as RestUpdateProduct["barcode"],
      );
    case "manufacturerArticleNumber":
      return (
        normalizeOptionalString(value as string) !==
        normalizeOptionalString(product.manufacturerArticleNumber)
      );
    case "manufacturerBarcode":
      return (
        normalizeOptionalString(value as string) !==
        normalizeOptionalString(product.manufacturerBarcode)
      );
    case "extraFields":
      return !extraFieldsEqual(
        product.extraFields,
        value as Record<string, string> | undefined,
      );
    case "prepStationId":
      return !nullableIdEqual(product.prepStationId, value as string | null);
    case "courseId":
      return !nullableIdEqual(product.courseId, value as string | null);
    case "requireReference":
      return value !== product.requireReference;
    case "takeawayVAT":
      return (value as number | null) !== (product.takeawayVAT ?? null);
    case "takeawayBookkeepingAccountId":
      return !nullableIdEqual(
        product.takeawayBookkeepingAccountId,
        value as string | null,
      );
    case "takeawayPrice":
      return !moneyEqual(product.takeawayPrice ?? null, value as Money | null);
    case "description":
      return (
        normalizeOptionalString(value as string) !==
        normalizeOptionalString(product.description)
      );
    case "variants":
      return !variantsEqual(product.variants, value as ProductVariant[]);
    default:
      return true;
  }
};

export const isProductFieldDirty = (
  product: RestProduct,
  edits: RestUpdateProduct | undefined,
  field: keyof RestUpdateProduct,
): boolean => {
  if (!edits || !(field in edits)) {
    return false;
  }
  return isProductFieldValueChanged(product, field, edits[field]);
};

export const isProductExtraFieldDirty = (
  product: RestProduct,
  edits: RestUpdateProduct | undefined,
  key: string,
): boolean => {
  if (!edits?.extraFields || !(key in edits.extraFields)) {
    return false;
  }
  const merged = { ...(product.extraFields ?? {}), ...edits.extraFields };
  return (merged[key] ?? "") !== (product.extraFields?.[key] ?? "");
};

export const isVariantFieldDirty = (
  product: RestProduct,
  edits: RestUpdateProduct | undefined,
  variantId: ProductVariantId,
  field: EditableVariantField,
): boolean => {
  const edited = edits?.variants?.find((variant) => variant.id === variantId);
  if (!edited) {
    return false;
  }
  const original = product.variants?.find(
    (variant) => variant.id === variantId,
  );
  return isVariantFieldValueChanged(original, edited, field);
};

export const isProductBarcodeDirty = (
  product: RestProduct,
  edits: RestUpdateProduct | undefined,
): boolean => isProductFieldDirty(product, edits, "barcode");

export const pruneUnchangedProductEdits = (
  product: RestProduct,
  edits: RestUpdateProduct,
): RestUpdateProduct => {
  const pruned: RestUpdateProduct = {};
  (Object.keys(edits) as (keyof RestUpdateProduct)[]).forEach((field) => {
    const value = edits[field];
    if (isProductFieldValueChanged(product, field, value)) {
      (pruned as Record<string, unknown>)[field] = value;
    }
  });
  return pruned;
};

export const toOverlayUpdate = (
  product: RestProduct,
  changes: RestUpdateProduct,
): RestUpsertProductOverlay => {
  const overlay = RestUpsertProductOverlay.parse(
    Object.fromEntries(
      ProductOverlayFieldScheme.options
        .filter((field) => isProductFieldDirty(product, changes, field))
        .map((field) => [field, changes[field]]),
    ),
  );
  const variants = Object.fromEntries(
    (changes.variants ?? []).flatMap((variant) => {
      const fields = ProductVariantOverlayFieldScheme.options.filter((field) =>
        isVariantFieldDirty(product, changes, variant.id, field),
      );
      return fields.length > 0
        ? [
            [
              variant.id,
              Object.fromEntries(
                fields.map((field) => [field, variant[field] ?? null]),
              ),
            ],
          ]
        : [];
    }),
  );
  return Object.keys(variants).length > 0 ? { ...overlay, variants } : overlay;
};
